import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow, MaskLineView, Reveal } from "../components/ui-bits";
import { useLang, PAGES } from "../i18n/LangContext";

const COPY = {
  en: {
    eyebrow: "Overview",
    h: ["Every page,", "in one place."],
    groups: [
      {
        titleKey: "products",
        links: [
          { to: "/products/pre-construction-finance", d: "Land acquisition, licensing and pre-sale bridge loans." },
          { to: "/apply", d: "Describe your project — Verde, our AI underwriter, builds your case." },
        ],
      },
      {
        titleKey: "company",
        links: [
          { to: "/about", d: "Who we are and how we work." },
          { to: "/contact", d: "Phone, WhatsApp and a direct form." },
          { to: "/book", d: "Pick a slot that suits you — confirmed instantly." },
          { to: "/expert-network", d: "The local specialists behind every credit decision." },
        ],
      },
      {
        titleKey: "information",
        links: [
          { to: "/brokers", d: "Introduce a deal — we pay you, not the developer." },
          { to: "/brokers/submit", d: "Send an opportunity straight to the team." },
          { to: "/investors", d: "An 8% annual yield, secured on Portuguese real estate." },
        ],
      },
      {
        titleKey: "infoLegal",
        links: [
          { to: "/faq", label: "Frequently asked questions", d: "Answers for developers, investors and brokers." },
          { to: "/privacy", label: "Privacy", d: "How we handle your data." },
          { to: "/terms", label: "Terms", d: "Website terms of use." },
          { to: "/cookies", label: "Cookies", d: "What we set and why." },
          { to: "/lending-disclaimer", label: "Lending disclaimer", d: "The important small print." },
        ],
      },
    ],
  },
  pt: {
    eyebrow: "Visão geral",
    h: ["Todas as páginas,", "num só lugar."],
    groups: [
      {
        titleKey: "products",
        links: [
          { to: "/products/pre-construction-finance", d: "Aquisição de terreno, licenciamento e ponte para pré-vendas." },
          { to: "/apply", d: "Descreva o seu projeto — a Verde, a nossa subscritora de IA, constrói o seu processo." },
        ],
      },
      {
        titleKey: "company",
        links: [
          { to: "/about", d: "Quem somos e como trabalhamos." },
          { to: "/contact", d: "Telefone, WhatsApp e um formulário direto." },
          { to: "/book", d: "Escolha um horário que lhe sirva — confirmação instantânea." },
          { to: "/expert-network", d: "Os especialistas locais por trás de cada decisão de crédito." },
        ],
      },
      {
        titleKey: "information",
        links: [
          { to: "/brokers", d: "Apresente um negócio — pagamos-lhe a si, não ao promotor." },
          { to: "/brokers/submit", d: "Envie uma oportunidade diretamente à equipa." },
          { to: "/investors", d: "Uma rentabilidade anual de 8%, garantida sobre imobiliário português." },
        ],
      },
      {
        titleKey: "infoLegal",
        links: [
          { to: "/faq", label: "Perguntas frequentes", d: "Respostas para promotores, investidores e intermediários." },
          { to: "/privacy", label: "Privacidade", d: "Como tratamos os seus dados." },
          { to: "/terms", label: "Termos", d: "Termos de utilização do website." },
          { to: "/cookies", label: "Cookies", d: "O que utilizamos e porquê." },
          { to: "/lending-disclaimer", label: "Aviso sobre crédito", d: "A letra pequena importante." },
        ],
      },
    ],
  },
  nl: {
    eyebrow: "Overzicht",
    h: ["Elke pagina,", "op één plek."],
    groups: [
      {
        titleKey: "products",
        links: [
          { to: "/products/pre-construction-finance", d: "Grondverwerving, vergunningen en brugfinanciering voor voorverkoop." },
          { to: "/apply", d: "Beschrijf uw project — Verde, onze AI-underwriter, bouwt uw dossier." },
        ],
      },
      {
        titleKey: "company",
        links: [
          { to: "/about", d: "Wie wij zijn en hoe wij werken." },
          { to: "/contact", d: "Telefoon, WhatsApp en een direct formulier." },
          { to: "/book", d: "Kies een moment dat u schikt — direct bevestigd." },
          { to: "/expert-network", d: "De lokale specialisten achter elke kredietbeslissing." },
        ],
      },
      {
        titleKey: "information",
        links: [
          { to: "/brokers", d: "Introduceer een deal — wij betalen u, niet de ontwikkelaar." },
          { to: "/brokers/submit", d: "Stuur een kans rechtstreeks naar het team." },
          { to: "/investors", d: "Een jaarrendement van 8%, gedekt op Portugees vastgoed." },
        ],
      },
      {
        titleKey: "infoLegal",
        links: [
          { to: "/faq", label: "Veelgestelde vragen", d: "Antwoorden voor ontwikkelaars, investeerders en tussenpersonen." },
          { to: "/privacy", label: "Privacy", d: "Hoe wij uw gegevens behandelen." },
          { to: "/terms", label: "Voorwaarden", d: "Gebruiksvoorwaarden van de website." },
          { to: "/cookies", label: "Cookies", d: "Wat wij plaatsen en waarom." },
          { to: "/lending-disclaimer", label: "Kredietdisclaimer", d: "De belangrijke kleine lettertjes." },
        ],
      },
    ],
  },
};

export default function Sitemap() {
  const { lang, lp } = useLang();
  const c = COPY[lang];
  return (
    <div className="bg-cream">
      <section className="pt-32 sm:pt-40 pb-14">
        <div className="container-x max-w-3xl">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h1 className="display mt-6 text-[2.6rem] sm:text-5xl lg:text-[56px] text-charcoal" data-testid="sitemap-headline">
            <MaskLineView>{c.h[0]}</MaskLineView>
            <MaskLineView delay={0.12}>{c.h[1]}</MaskLineView>
          </h1>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="container-x grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {c.groups.map((g, gi) => (
            <Reveal key={g.titleKey} delay={0.05 + gi * 0.06}>
              <div className="border-t border-charcoal/15 pt-6" data-testid={`sitemap-group-${gi}`}>
                <h2 className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-verde-700">
                  {g.titleKey === "infoLegal" ? (lang === "pt" ? "Informação e legal" : lang === "nl" ? "Informatie & legaal" : "Information & legal") : CHROME_LABELS[lang][g.titleKey]}
                </h2>
                <ul className="mt-5 space-y-4">
                  {g.links.map((l) => {
                    const label = l.label || PAGES[l.to][lang];
                    return (
                      <li key={l.to}>
                        <Link
                          to={lp(l.to)}
                          data-testid={`sitemap-link-${(PAGES[l.to].en || l.label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="group block"
                        >
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal transition-colors group-hover:text-verde-700">
                            {label}
                            <ArrowRight className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
                          </span>
                          <span className="mt-1 block text-xs leading-relaxed text-fog">{l.d}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

const CHROME_LABELS = {
  en: { products: "Products", company: "Company", information: "Partner with us" },
  pt: { products: "Produtos", company: "Empresa", information: "Seja parceiro" },
  nl: { products: "Producten", company: "Bedrijf", information: "Word partner" },
};
